/**
 * Stats Overview Component
 * Displays annotation statistics and charts
 */

import type { StatsResponse } from '@/types/annotation';
import { BarChart3, Users, CheckCircle, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';

interface StatsOverviewProps {
  stats: StatsResponse;
  onExportGolden?: () => void;
  isExporting?: boolean;
}

export function StatsOverview({ stats, onExportGolden, isExporting }: StatsOverviewProps) {
  const { t } = useI18n();
  const { overview, by_batch, by_annotator } = stats;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<FileText className="w-6 h-6" />}
          label={t('statsOverview.cards.totalBatches')}
          value={overview.total_batches}
          subValue={`${overview.active_batches} ${t('statsOverview.cards.activeBatches')}`}
          color="blue"
        />
        <StatCard
          icon={<BarChart3 className="w-6 h-6" />}
          label={t('statsOverview.cards.totalTasks')}
          value={overview.total_tasks}
          subValue={`${overview.pending_tasks} ${t('statsOverview.cards.pendingTasks')}`}
          color="indigo"
        />
        <StatCard
          icon={<CheckCircle className="w-6 h-6" />}
          label={t('statsOverview.cards.completedTasks')}
          value={overview.completed_tasks}
          subValue={`${t('statsOverview.cards.completionRate')} ${overview.completion_rate.toFixed(1)}%`}
          color="green"
        />
        <StatCard
          icon={<Users className="w-6 h-6" />}
          label={t('statsOverview.cards.annotators')}
          value={overview.total_annotators}
          subValue={
            overview.avg_completion_time_seconds
              ? `${t('statsOverview.cards.avgTime')} ${Math.round(overview.avg_completion_time_seconds)}s`
              : '-'
          }
          color="purple"
        />
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('statsOverview.overallProgress')}</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('statsOverview.completed')} {overview.completed_tasks} / {overview.total_tasks}</span>
            <span className="font-medium text-gray-900">{overview.completion_rate.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="h-4 rounded-full bg-gradient-to-r from-green-400 to-green-600 transition-all"
              style={{ width: `${Math.min(overview.completion_rate, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Batch Stats Table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('statsOverview.batchStats.title')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-gray-200">
                <th className="pb-3 font-medium text-gray-600">{t('statsOverview.batchStats.batch')}</th>
                <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.batchStats.totalTasks')}</th>
                <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.batchStats.completed')}</th>
                <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.batchStats.pending')}</th>
                <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.batchStats.progress')}</th>
              </tr>
            </thead>
            <tbody>
              {by_batch.map((batch) => (
                <tr key={batch.batch_id} className="border-b border-gray-100 last:border-0">
                  <td className="py-3">
                    <div className="font-medium text-gray-900">{batch.title}</div>
                    <div className="text-xs text-gray-500">{batch.batch_id}</div>
                  </td>
                  <td className="py-3 text-right text-gray-900">{batch.total_tasks}</td>
                  <td className="py-3 text-right text-green-600">{batch.completed_tasks}</td>
                  <td className="py-3 text-right text-amber-600">{batch.pending_tasks}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-primary-500"
                          style={{ width: `${batch.progress_percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-12 text-right">
                        {batch.progress_percent.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Annotator Stats Table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('statsOverview.annotatorStats.title')}</h3>
        {by_annotator.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-200">
                  <th className="pb-3 font-medium text-gray-600">{t('statsOverview.annotatorStats.annotator')}</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.annotatorStats.completed')}</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.annotatorStats.skipped')}</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.annotatorStats.avgTime')}</th>
                  <th className="pb-3 font-medium text-gray-600 text-right">{t('statsOverview.annotatorStats.recentActivity')}</th>
                </tr>
              </thead>
              <tbody>
                {by_annotator.map((annotator) => (
                  <tr key={annotator.annotator_id} className="border-b border-gray-100 last:border-0">
                    <td className="py-3">
                      <span className="font-medium text-gray-900">
                        {annotator.annotator_id.slice(0, 12)}...
                      </span>
                    </td>
                    <td className="py-3 text-right text-green-600">{annotator.total_completed}</td>
                    <td className="py-3 text-right text-gray-500">{annotator.total_skipped}</td>
                    <td className="py-3 text-right text-gray-600">
                      {annotator.avg_time_seconds
                        ? `${Math.round(annotator.avg_time_seconds)}s`
                        : '-'}
                    </td>
                    <td className="py-3 text-right text-gray-500 text-sm">
                      {annotator.recent_activity
                        ? new Date(annotator.recent_activity).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t('statsOverview.annotatorStats.noData')}
          </div>
        )}
      </div>

      {/* Export Golden Cases */}
      {overview.completed_tasks > 0 && onExportGolden && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('statsOverview.export.title')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('statsOverview.export.description')}
              </p>
            </div>
            <Button variant="primary" onClick={onExportGolden} loading={isExporting}>
              <Download className="w-4 h-4 mr-2" />
              {t('statsOverview.export.button')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  subValue?: string;
  color: 'blue' | 'green' | 'indigo' | 'purple' | 'amber';
}

const colorClasses = {
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  purple: 'bg-purple-100 text-purple-600',
  amber: 'bg-amber-100 text-amber-600',
};

function StatCard({ icon, label, value, subValue, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-600">{label}</div>
          {subValue && <div className="text-xs text-gray-500">{subValue}</div>}
        </div>
      </div>
    </div>
  );
}
